import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Drawer from 'react-modern-drawer';
import { X } from 'lucide-react';

import { ReactComponent as FiltersIcon } from '../../../assets/images/filters.svg';
import Button from '../../../components/shared/Button/Button';
import { useWindowSize } from '../../../hooks/useWindowResize';

import 'react-modern-drawer/dist/index.css';
import styles from './TrackerFiltersDrawer.module.scss';

const TrackerFiltersDrawer = ({ open, onClose, filters, onApplyFilters, filtersCount, direction }) => {
  const { t } = useTranslation('common');
  const size = useWindowSize();

  // Local filter state (only committed on "Apply")
  const [localFilters, setLocalFilters] = useState(filters);
  const [positionInput, setPositionInput] = useState('');
  const [companyInput, setCompanyInput] = useState('');

  useEffect(() => {
    if (open) {
      setLocalFilters(filters);
    }
  }, [open, filters]);

  const drawerWidth = size.width > 450 ? 375 : size.width;

  const handleApply = useCallback(() => {
    onApplyFilters(localFilters);
    onClose();
  }, [localFilters, onApplyFilters, onClose]);

  const handleReset = useCallback(() => {
    const empty = {};
    setLocalFilters(empty);
    onApplyFilters(empty);
    setPositionInput('');
    setCompanyInput('');
  }, [onApplyFilters]);

  const addPosition = () => {
    if (positionInput.trim()) {
      setLocalFilters((prev) => ({
        ...prev,
        positions: [...(prev.positions || []), positionInput.trim()],
      }));
      setPositionInput('');
    }
  };

  const removePosition = (idx) => {
    setLocalFilters((prev) => ({
      ...prev,
      positions: (prev.positions || []).filter((_, i) => i !== idx),
    }));
  };

  const addCompany = () => {
    if (companyInput.trim()) {
      setLocalFilters((prev) => ({
        ...prev,
        companies: [...(prev.companies || []), companyInput.trim()],
      }));
      setCompanyInput('');
    }
  };

  const removeCompany = (idx) => {
    setLocalFilters((prev) => ({
      ...prev,
      companies: (prev.companies || []).filter((_, i) => i !== idx),
    }));
  };

  const handleKeyDown = (e, addFn) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addFn();
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size={drawerWidth}
      duration={200}
      zIndex={999999}
      style={{ height: '100%' }}
    >
      <div className={styles.drawerContainer}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <FiltersIcon />
            <span>{t('tracker.filters', 'Filters')}</span>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.divider} />

        {/* Content */}
        <div className={styles.content}>
          {filtersCount > 0 && (
            <button type="button" className={styles.clearAll} onClick={handleReset}>
              {t('tracker.clearAll', 'Clear All')}
            </button>
          )}

          {/* Position / Job Title Filter */}
          <div className={styles.filterSection}>
            <label className={styles.filterLabel}>
              {t('tracker.filterByPosition', 'Job Title / Position')}
            </label>
            <div className={styles.inputRow}>
              <input
                type="text"
                className={styles.filterInput}
                placeholder={t('tracker.typeAndEnter', 'Type and press Enter...')}
                value={positionInput}
                onChange={(e) => setPositionInput(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, addPosition)}
              />
              <button type="button" className={styles.addBtn} onClick={addPosition}>
                +
              </button>
            </div>
            {localFilters.positions?.length > 0 && (
              <div className={styles.chips}>
                {localFilters.positions.map((p, i) => (
                  <span key={i} className={styles.chip}>
                    {p}
                    <button type="button" onClick={() => removePosition(i)}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Company Filter */}
          <div className={styles.filterSection}>
            <label className={styles.filterLabel}>
              {t('tracker.filterByCompany', 'Company / Organization')}
            </label>
            <div className={styles.inputRow}>
              <input
                type="text"
                className={styles.filterInput}
                placeholder={t('tracker.typeAndEnter', 'Type and press Enter...')}
                value={companyInput}
                onChange={(e) => setCompanyInput(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, addCompany)}
              />
              <button type="button" className={styles.addBtn} onClick={addCompany}>
                +
              </button>
            </div>
            {localFilters.companies?.length > 0 && (
              <div className={styles.chips}>
                {localFilters.companies.map((c, i) => (
                  <span key={i} className={styles.chip}>
                    {c}
                    <button type="button" onClick={() => removeCompany(i)}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Apply button */}
          <div className={styles.applyContainer}>
            <Button onClick={handleApply}>{t('tracker.showResults', 'Show Results')}</Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default TrackerFiltersDrawer;
